import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Flame,
  LogOut,
  Download,
  Trash2,
  Check,
  RefreshCw,
  Search,
  Mail,
  Phone,
  Calendar as CalendarIcon,
  MessageSquare,
  Users,
  ShoppingBag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'act_admin_password';

const LoginGate = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/admin/verify`, null, {
        headers: { 'X-Admin-Password': password },
      });
      localStorage.setItem(STORAGE_KEY, password);
      onSuccess(password);
    } catch (err) {
      setError('Incorrect password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1030] flex items-center justify-center px-4">
      <form
        onSubmit={handleLogin}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-10"
      >
        <div className="flex items-center gap-2 mb-6">
          <Flame className="h-7 w-7 text-[#0a1030]" strokeWidth={2.5} />
          <span className="font-extrabold tracking-wide text-[15px] text-[#0a1030]">
            ACT ADMIN
          </span>
        </div>
        <h1
          className="text-3xl mb-2 text-[#0a1030]"
          style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
        >
          Welcome back
        </h1>
        <p className="text-sm text-[#5a5a5a] mb-6">
          Enter the shared admin password to view visitor submissions.
        </p>
        <Label htmlFor="pw" className="text-[13px] font-medium">
          Admin Password
        </Label>
        <Input
          id="pw"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mt-1.5 h-11"
          autoFocus
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <Button
          type="submit"
          disabled={loading || !password}
          className="mt-6 w-full rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white h-12"
        >
          {loading ? 'Checking…' : 'Sign in'}
        </Button>
        <p className="mt-4 text-xs text-center text-[#7a7a7a]">
          Trouble signing in? Ask the site owner for the current password.
        </p>
      </form>
    </div>
  );
};

const StatusBadge = ({ status }) =>
  status === 'new' ? (
    <Badge className="bg-[#0a1030] hover:bg-[#0a1030] text-white text-[10px] rounded-full">
      NEW
    </Badge>
  ) : (
    <Badge className="bg-green-100 hover:bg-green-100 text-green-800 text-[10px] rounded-full">
      REPLIED
    </Badge>
  );

const VisitsTable = ({ password, toast, onCountChange }) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const authHeaders = { 'X-Admin-Password': password };

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/visits`, { headers: authHeaders });
      setVisits(res.data);
      onCountChange?.(res.data.filter((v) => v.status === 'new').length);
    } catch (err) {
      toast({ title: 'Failed to load visits' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStatus = async (v) => {
    const newStatus = v.status === 'contacted' ? 'new' : 'contacted';
    try {
      const res = await axios.patch(
        `${API}/visits/${v.id}`,
        { status: newStatus },
        { headers: authHeaders }
      );
      setVisits((prev) => prev.map((x) => (x.id === v.id ? res.data : x)));
      onCountChange?.(
        visits
          .map((x) => (x.id === v.id ? res.data : x))
          .filter((v) => v.status === 'new').length
      );
    } catch (err) {
      toast({ title: 'Update failed' });
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/visits/${confirmDelete.id}`, {
        headers: authHeaders,
      });
      setVisits((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      toast({ title: 'Deleted', description: `${confirmDelete.name} removed.` });
    } catch (err) {
      toast({ title: 'Delete failed' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/visits/export`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `act_visits_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast({ title: 'Export failed' });
    }
  };

  const filtered = visits.filter((v) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      v.name?.toLowerCase().includes(s) ||
      v.email?.toLowerCase().includes(s) ||
      v.phone?.toLowerCase().includes(s) ||
      v.hear_about?.toLowerCase().includes(s)
    );
  });

  const newCount = visits.filter((v) => v.status === 'new').length;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="text-sm text-[#4a4a4a]">
          {visits.length} total ·{' '}
          <span className="text-[#0a1030] font-semibold">{newCount}</span> new
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7a7a]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone…"
              className="pl-9 h-10 w-[260px] bg-white"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchVisits}
            className="h-10"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button
            onClick={exportCsv}
            className="rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white h-10"
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#7a7a7a]">Loading submissions…</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#f4f2ef] flex items-center justify-center mb-4">
              <CalendarIcon className="h-6 w-6 text-[#0a1030]" />
            </div>
            <h3
              className="text-2xl text-[#0a1030]"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
            >
              {visits.length === 0 ? 'No visits yet' : 'No matches'}
            </h3>
            <p className="text-sm text-[#5a5a5a] mt-1">
              {visits.length === 0
                ? 'New Plan a Visit submissions will appear here.'
                : 'Try a different search term.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-[#faf9f7]">
              <TableRow>
                <TableHead className="w-[80px]">Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Visiting</TableHead>
                <TableHead>Heard About</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((v) => (
                <TableRow key={v.id} className="hover:bg-[#faf9f7]">
                  <TableCell>
                    {v.status === 'new' ? (
                      <Badge className="bg-[#0a1030] hover:bg-[#0a1030] text-white text-[10px] rounded-full">
                        NEW
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 hover:bg-green-100 text-green-800 text-[10px] rounded-full">
                        CONTACTED
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-[#0a1030]">{v.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5 text-xs">
                      <a
                        href={`mailto:${v.email}`}
                        className="flex items-center gap-1.5 text-[#0a1030] hover:underline"
                      >
                        <Mail className="h-3 w-3" /> {v.email}
                      </a>
                      <a
                        href={`tel:${v.phone.replace(/[^0-9+]/g, '')}`}
                        className="flex items-center gap-1.5 text-[#4a4a4a] hover:text-[#0a1030]"
                      >
                        <Phone className="h-3 w-3" /> {v.phone}
                      </a>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {new Date(v.sunday + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell className="text-xs text-[#5a5a5a] max-w-[220px]">
                    {v.hear_about}
                  </TableCell>
                  <TableCell className="text-xs text-[#7a7a7a]">
                    {new Date(v.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStatus(v)}
                        className="h-8 px-2 text-[#0a1030]"
                        title={v.status === 'new' ? 'Mark as contacted' : 'Mark as new'}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDelete(v)}
                        className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{' '}
              <span className="font-semibold">{confirmDelete?.name}</span>'s visit
              request. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const MessagesTable = ({ password, toast, onCountChange }) => {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [view, setView] = useState(null);
  const authHeaders = { 'X-Admin-Password': password };

  const fetchMsgs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages`, { headers: authHeaders });
      setMsgs(res.data);
      onCountChange?.(res.data.filter((m) => m.status === 'new').length);
    } catch (err) {
      toast({ title: 'Failed to load messages' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMsgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStatus = async (m) => {
    const newStatus = m.status === 'replied' ? 'new' : 'replied';
    try {
      const res = await axios.patch(
        `${API}/messages/${m.id}`,
        { status: newStatus },
        { headers: authHeaders }
      );
      const next = msgs.map((x) => (x.id === m.id ? res.data : x));
      setMsgs(next);
      onCountChange?.(next.filter((v) => v.status === 'new').length);
    } catch (err) {
      toast({ title: 'Update failed' });
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/messages/${confirmDelete.id}`, {
        headers: authHeaders,
      });
      setMsgs((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      toast({ title: 'Deleted', description: `Message from ${confirmDelete.name} removed.` });
    } catch (err) {
      toast({ title: 'Delete failed' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const filtered = msgs.filter((m) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      m.name?.toLowerCase().includes(s) ||
      m.email?.toLowerCase().includes(s) ||
      m.subject?.toLowerCase().includes(s) ||
      m.message?.toLowerCase().includes(s)
    );
  });

  const newCount = msgs.filter((m) => m.status === 'new').length;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="text-sm text-[#4a4a4a]">
          {msgs.length} total ·{' '}
          <span className="text-[#0a1030] font-semibold">{newCount}</span> new
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7a7a]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, subject, message…"
              className="pl-9 h-10 w-[260px] bg-white"
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchMsgs} className="h-10">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#7a7a7a]">Loading messages…</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#f4f2ef] flex items-center justify-center mb-4">
              <MessageSquare className="h-6 w-6 text-[#0a1030]" />
            </div>
            <h3
              className="text-2xl text-[#0a1030]"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
            >
              {msgs.length === 0 ? 'No messages yet' : 'No matches'}
            </h3>
            <p className="text-sm text-[#5a5a5a] mt-1">
              {msgs.length === 0
                ? 'Contact form submissions will appear here.'
                : 'Try a different search term.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-[#faf9f7]">
              <TableRow>
                <TableHead className="w-[80px]">Status</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Received</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((m) => (
                <TableRow
                  key={m.id}
                  className="hover:bg-[#faf9f7] cursor-pointer"
                  onClick={() => setView(m)}
                >
                  <TableCell>
                    <StatusBadge status={m.status} />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-[#0a1030]">{m.name}</div>
                    <div className="text-xs text-[#5a5a5a]">{m.email}</div>
                  </TableCell>
                  <TableCell className="font-medium text-[#0a1030] max-w-[220px] truncate">
                    {m.subject}
                  </TableCell>
                  <TableCell className="text-xs text-[#5a5a5a] max-w-[300px] truncate">
                    {m.message}
                  </TableCell>
                  <TableCell className="text-xs text-[#7a7a7a]">
                    {new Date(m.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStatus(m)}
                        className="h-8 px-2 text-[#0a1030]"
                        title={m.status === 'new' ? 'Mark as replied' : 'Mark as new'}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDelete(m)}
                        className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif', fontSize: 26 }}>
              {view?.subject}
            </DialogTitle>
            <DialogDescription>
              From <span className="font-semibold text-[#0a1030]">{view?.name}</span>{' '}
              &lt;{view?.email}&gt;
              {view?.phone ? ` · ${view.phone}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <p className="whitespace-pre-wrap text-[#1a1a1a] leading-relaxed">{view?.message}</p>
            <div className="mt-4 text-xs text-[#7a7a7a]">
              Received {view && new Date(view.created_at).toLocaleString()}
            </div>
            <div className="mt-6 flex gap-2">
              <Button asChild className="rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white">
                <a href={`mailto:${view?.email}?subject=Re: ${encodeURIComponent(view?.subject || '')}`}>
                  <Mail className="h-4 w-4 mr-2" /> Reply by email
                </a>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the message from{' '}
              <span className="font-semibold">{confirmDelete?.name}</span>. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const OrdersTable = ({ password, toast, onCountChange }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [view, setView] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const authHeaders = { 'X-Admin-Password': password };

  const STATUS_LABELS = {
    pending_payment: { text: 'PENDING', cls: 'bg-amber-100 text-amber-800' },
    paid: { text: 'PAID', cls: 'bg-[#0a1030] text-white' },
    ready: { text: 'READY', cls: 'bg-blue-100 text-blue-800' },
    picked_up: { text: 'PICKED UP', cls: 'bg-green-100 text-green-800' },
    cancelled: { text: 'CANCELLED', cls: 'bg-gray-200 text-gray-700' },
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders`, { headers: authHeaders });
      setOrders(res.data);
      onCountChange?.(res.data.filter((o) => o.status === 'pending_payment' || o.status === 'paid').length);
    } catch {
      toast({ title: 'Failed to load orders' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (o, status) => {
    try {
      const res = await axios.patch(`${API}/orders/${o.id}`, { status }, { headers: authHeaders });
      const next = orders.map((x) => (x.id === o.id ? res.data : x));
      setOrders(next);
      onCountChange?.(next.filter((v) => v.status === 'pending_payment' || v.status === 'paid').length);
      if (view?.id === o.id) setView(res.data);
    } catch {
      toast({ title: 'Update failed' });
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/orders/${confirmDelete.id}`, { headers: authHeaders });
      setOrders((prev) => prev.filter((x) => x.id !== confirmDelete.id));
    } catch {
      toast({ title: 'Delete failed' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const filtered = orders.filter((o) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      o.customer_name?.toLowerCase().includes(s) ||
      o.customer_email?.toLowerCase().includes(s) ||
      o.customer_phone?.toLowerCase().includes(s) ||
      o.id?.toLowerCase().includes(s)
    );
  });

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="text-sm text-[#4a4a4a]">{orders.length} total</div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7a7a]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, order#…"
              className="pl-9 h-10 w-[260px] bg-white"
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchOrders} className="h-10">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#7a7a7a]">Loading orders…</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#f4f2ef] flex items-center justify-center mb-4">
              <ShoppingBag className="h-6 w-6 text-[#0a1030]" />
            </div>
            <h3 className="text-2xl text-[#0a1030]" style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
              {orders.length === 0 ? 'No orders yet' : 'No matches'}
            </h3>
            <p className="text-sm text-[#5a5a5a] mt-1">
              {orders.length === 0 ? 'Merch orders will appear here.' : 'Try a different search term.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-[#faf9f7]">
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Placed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((o) => {
                const lbl = STATUS_LABELS[o.status] || STATUS_LABELS.pending_payment;
                return (
                  <TableRow key={o.id} className="hover:bg-[#faf9f7] cursor-pointer" onClick={() => setView(o)}>
                    <TableCell>
                      <Badge className={`${lbl.cls} hover:${lbl.cls} text-[10px] rounded-full`}>
                        {lbl.text}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[#0a1030]">
                      {o.id.slice(0, 8).toUpperCase()}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-[#0a1030]">{o.customer_name}</div>
                      <div className="text-xs text-[#5a5a5a]">{o.customer_email}</div>
                    </TableCell>
                    <TableCell className="text-xs text-[#5a5a5a]">
                      {o.items.reduce((n, i) => n + i.quantity, 0)} item{o.items.reduce((n, i) => n + i.quantity, 0) === 1 ? '' : 's'}
                    </TableCell>
                    <TableCell className="font-semibold text-[#0a1030]">${o.subtotal.toFixed(2)}</TableCell>
                    <TableCell className="text-xs text-[#7a7a7a]">
                      {new Date(o.created_at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(o)}
                          className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif', fontSize: 24 }}>
              Order #{view?.id.slice(0, 8).toUpperCase()}
            </DialogTitle>
            <DialogDescription>
              {view?.customer_name} · {view?.customer_email} · {view?.customer_phone}
            </DialogDescription>
          </DialogHeader>
          {view && (
            <div className="space-y-4">
              <div className="divide-y divide-black/5">
                {view.items.map((i, idx) => (
                  <div key={idx} className="py-2 flex justify-between text-sm">
                    <div>
                      <div className="font-medium text-[#0a1030]">{i.product_name}</div>
                      <div className="text-xs text-[#7a7a7a]">Size {i.size} · Qty {i.quantity}</div>
                    </div>
                    <div className="font-semibold">${(i.price * i.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-semibold text-[#0a1030] pt-2 border-t border-black/10">
                <span>Total</span>
                <span>${view.subtotal.toFixed(2)}</span>
              </div>
              {view.notes && (
                <div className="text-sm bg-[#faf7f2] p-3 rounded border border-black/5">
                  <div className="text-xs font-semibold text-[#8a7a4a] uppercase tracking-wider mb-1">Notes</div>
                  {view.notes}
                </div>
              )}
              <div>
                <div className="text-xs font-semibold text-[#0a1030] uppercase tracking-wider mb-2">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_LABELS).map(([key, lbl]) => (
                    <button
                      key={key}
                      onClick={() => setStatus(view, key)}
                      className={`text-[11px] px-3 py-1.5 rounded-full font-semibold ${
                        view.status === key ? lbl.cls : 'bg-white border border-black/15 text-[#3a3a3a] hover:border-[#0a1030]'
                      }`}
                    >
                      {lbl.text}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Dashboard = ({ password, onLogout }) => {
  const { toast } = useToast();
  const [visitNew, setVisitNew] = useState(0);
  const [msgNew, setMsgNew] = useState(0);
  const [orderNew, setOrderNew] = useState(0);

  return (
    <div className="min-h-screen bg-[#f4f2ef]">
      <header className="bg-[#0a1030] text-white sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <div className="flex items-center gap-2">
            <Flame className="h-6 w-6" strokeWidth={2.5} />
            <span className="font-extrabold tracking-wide text-[14px]">ACT ADMIN</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="text-white hover:bg-white/10"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <h1
            className="text-4xl md:text-5xl text-[#0a1030]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
          >
            Dashboard
          </h1>
          <p className="mt-2 text-[#4a4a4a] text-sm">
            Manage visit requests and contact messages in one place.
          </p>
        </div>

        <Tabs defaultValue="visits" className="w-full">
          <TabsList className="bg-white border border-black/5 rounded-full p-1 h-auto">
            <TabsTrigger
              value="visits"
              className="rounded-full px-5 py-2 data-[state=active]:bg-[#0a1030] data-[state=active]:text-white flex items-center gap-2"
            >
              <Users className="h-4 w-4" /> Visit Requests
              {visitNew > 0 && (
                <span className="ml-1 bg-[#e9d9a0] text-[#0a1030] text-[10px] font-bold rounded-full px-2 py-0.5">
                  {visitNew}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              className="rounded-full px-5 py-2 data-[state=active]:bg-[#0a1030] data-[state=active]:text-white flex items-center gap-2"
            >
              <MessageSquare className="h-4 w-4" /> Messages
              {msgNew > 0 && (
                <span className="ml-1 bg-[#e9d9a0] text-[#0a1030] text-[10px] font-bold rounded-full px-2 py-0.5">
                  {msgNew}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="orders"
              className="rounded-full px-5 py-2 data-[state=active]:bg-[#0a1030] data-[state=active]:text-white flex items-center gap-2"
            >
              <ShoppingBag className="h-4 w-4" /> Orders
              {orderNew > 0 && (
                <span className="ml-1 bg-[#e9d9a0] text-[#0a1030] text-[10px] font-bold rounded-full px-2 py-0.5">
                  {orderNew}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="visits" className="mt-6">
            <VisitsTable password={password} toast={toast} onCountChange={setVisitNew} />
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <MessagesTable password={password} toast={toast} onCountChange={setMsgNew} />
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <OrdersTable password={password} toast={toast} onCountChange={setOrderNew} />
          </TabsContent>
        </Tabs>
      </main>

      <Toaster />
    </div>
  );
};

const Admin = () => {
  const [password, setPassword] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [checking, setChecking] = useState(!!password);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!password) {
        setChecking(false);
        return;
      }
      try {
        await axios.post(`${API}/admin/verify`, null, {
          headers: { 'X-Admin-Password': password },
        });
        if (!cancelled) setAuthed(true);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(STORAGE_KEY);
          setPassword('');
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    verify();
    return () => {
      cancelled = true;
    };
  }, [password]);

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPassword('');
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a1030] flex items-center justify-center text-white text-sm">
        Checking session…
      </div>
    );
  }

  if (!authed) {
    return (
      <LoginGate
        onSuccess={(pw) => {
          setPassword(pw);
          setAuthed(true);
        }}
      />
    );
  }

  return <Dashboard password={password} onLogout={handleLogout} />;
};

export default Admin;
